import styled from "@emotion/styled";
import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import Text from "@components/Text";
import { usePoolInvestVM } from "./PoolInvestVM";
import Card from "@src/components/Card";
import { useStores } from "@stores";
import BN from "@src/utils/BN";
import arrowDownIcon from "@src/assets/icons/thingArrowDown.svg";
import { ReactComponent as InfoIcon } from "@src/assets/icons/info.svg";
import Tooltip from "@components/Tooltip";
import SizedBox from "@components/SizedBox";
import { Column, Row } from "@components/Flex";
import Button from "@components/Button";
import Divider from "@src/components/Divider";
import Switch from "@src/components/Switch";
import StakeUnstakeInput from "./StakeUnstakeInput";
import SwitchButtons from "@src/components/SwitchButtons";

interface IProps {}

const Root = styled(Card)`
  width: 100%;
  margin-top: 24px;
  padding: 0 !important;
`;

const Title = styled.span<{ expanded?: boolean }>`
  display: flex;
  align-items: center;
  width: calc(100% - 48px);
  margin: 16px 24px;
  transition: 0.4s;
  position: relative;
  weight: 500;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary800};

  :after {
    position: absolute;
    top: 4px;
    right: 0;
    height: 16px;
    content: url(${arrowDownIcon});
    transition: 0.4s;
    transform: rotate(${({ expanded }) => (expanded ? -180 : 0)}deg);
  }

  :hover {
    color: #7075e9;
    :after {
      transform: rotate(${({ expanded }) => (expanded ? -180 : -90)}deg);
    }
  }
`;

const Body = styled(Column)<{ expanded?: boolean }>`
  transition: 0.4s;
  overflow: hidden;
  width: 100%;
  height: ${({ expanded }) => (expanded ? "auto" : 0)};
`;

const Information = styled(Column)`
  width: 100%;
  padding: 16px 24px;
  padding-top: 0;
  box-sizing: border-box;
`;

const Actions = styled(Column)`
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;
`;

const LPStaking: React.FC<IProps> = () => {
  const { accountStore } = useStores();
  const vm = usePoolInvestVM();
  const [expanded, setExpanded] = useState(false);
  const activeTab = useMemo(() => vm.stakeUnstakeAction === "stake", [vm.stakeUnstakeAction]);
  const handleChangeActiveTab = (value: number) => {
    vm.setStakeUnstakeAction(value === 0 ? "stake" : "unstake");
  };

  if (accountStore.address == null) return null;
  const availableToStakeLP = BN.formatUnits(vm.indexTokenBalance, 8);
  const availableToStakeUsd = BN.formatUnits(vm.indexTokenBalance.times(vm.pool.indexTokenRate), 8);

  return (
    <Root>
      <Title expanded={expanded} onClick={() => setExpanded(!expanded)}>
        LP Staking
        <Tooltip
          containerStyles={{ display: "flex", alignItems: "center" }}
          content={<Text>Stake and unstake PZ Index token, which represents the value of your pool share</Text>}
        >
          <InfoIcon style={{ marginLeft: 8 }} />
        </Tooltip>
      </Title>
      <Body expanded={expanded}>
        <Information>
          <SwitchButtons values={["Stake", "Unstake"]} active={activeTab ? 0 : 1} onActivate={handleChangeActiveTab} />
          <SizedBox height={16} />
          {vm.stakeUnstakeAction === "unstake" && (
            <Column>
              <Text type="secondary" size="medium">
                Staked balance
              </Text>
              <Row alignItems="baseline">
                <Text nowrap>{(() => {
                  const stakedLP = BN.formatUnits(vm.userIndexStaked ?? BN.ZERO, 8);
                  if (stakedLP.eq(0)) return stakedLP.toFormat(2, BN.ROUND_DOWN);
                  const formatted = stakedLP.toFormat(8, BN.ROUND_DOWN);
                  return formatted.replace(/\.?0+$/, '');
                })()} LP</Text>
                <SizedBox width={4} />
                <Text type="secondary" size="small" style={{ display: "inline" }}>
                  / ${vm.totalProvidedLiquidityByAddress == null ? "0.00" : vm.totalProvidedLiquidityByAddress?.toFormat(2)}
                </Text>
              </Row>
            </Column>
          )}
          {vm.stakeUnstakeAction === "stake" && (
            <Column>
              <Text nowrap type="secondary" size="medium">
                Available to stake
              </Text>
              <Row alignItems="baseline">
                <Text nowrap>{(() => {
                  if (availableToStakeLP.eq(0)) return availableToStakeLP.toFormat(2, BN.ROUND_DOWN);
                  const formatted = availableToStakeLP.toFormat(8, BN.ROUND_DOWN);
                  return formatted.replace(/\.?0+$/, '');
                })()} LP</Text>
                <SizedBox width={4} />
                <Text type="secondary" size="small" style={{ display: "inline" }}>
                  / ${availableToStakeUsd.toFormat(2, BN.ROUND_DOWN)}
                </Text>
              </Row>
            </Column>
          )}
        </Information>

        <Divider />

        <Actions crossAxisSize="max">
          <Row>
            <Text size="medium" type="secondary">
              Use MAX
            </Text>
            <SizedBox width={16} />
            <Switch
              value={vm.useMaxStakeUnstakeAmount}
              onChange={() => {
                vm.setUseMaxStakeUnstakeAmount(!vm.useMaxStakeUnstakeAmount);
              }}
            />
          </Row>
          <SizedBox height={16} />
          {!vm.useMaxStakeUnstakeAmount && (
            <>
              <StakeUnstakeInput
                amount={vm.stakeUnstakeAmount}
                decimals={8}
                setAmount={vm.setStakeUnstakeAmount}
              />
              <SizedBox height={16} />
            </>
          )}
          {vm.stakeUnstakeAction === "unstake" && (
            <Button fixed kind="secondary" size="medium" disabled={!vm.canUnstakeIndex || vm.loading || vm.pool?.isRebalancing} onClick={vm.unstakeIndex}>
              Unstake
            </Button>
          )}
          {vm.stakeUnstakeAction === "stake" && (
            <Button fixed size="medium" disabled={!vm.canStakeIndex || vm.loading || vm.pool?.isRebalancing} onClick={vm.stakeIndex}>
              Stake
            </Button>
          )}
        </Actions>
      </Body>
    </Root>
  );
};
export default observer(LPStaking);
